import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AIInsightsPanelV2 from '../../features/ai-insights/components/AIInsightsPanelV2';
import { normalizeQuestionType } from '../../features/ai-insights/api/utils';

// Define supported question types based on EventQuestionType.js
const SUPPORTED_QUESTION_TYPES = [
  'default',      // OPEN_ENDED
  'binary',
  'multiple',     // MULTIPLE_CHOICE
  'multiple_rating',
  'scale',
  'scale_5',
  'scale_10',
  'stack_rank'
];

const useStyles = makeStyles((theme) => ({
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 200,
    gap: theme.spacing(2)
  },
  errorContainer: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
    textAlign: 'center'
  }
}));

/**
 * A wrapper component that provides access to the feature-based AI Insights implementation.
 * This component integrates with MainContent.jsx and provides enhanced AI analysis features.
 */
const AIInsightsFeaturebasedV1 = ({ eventId, event, stats }) => {
  const [currentQuestionType, setCurrentQuestionType] = useState(event?.question_type);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    // Update current question type when event changes
    if (event?.question_type !== currentQuestionType) {
      setCurrentQuestionType(event?.question_type);
      // console.log('Question type updated:', {
      //   previous: currentQuestionType,
      //   new: event?.question_type,
      //   eventId,
      //   timestamp: new Date().toISOString()
      // });
    }
  }, [event, currentQuestionType, eventId]);

  // Map question type using the normalized function
  const mappedQuestionType = normalizeQuestionType(currentQuestionType);

  // Log question type validation details
  // console.log('Question Type Validation:', {
  //   currentType: currentQuestionType,
  //   mappedType: mappedQuestionType,
  //   isValid: mappedQuestionType && SUPPORTED_QUESTION_TYPES.includes(mappedQuestionType),
  //   supportedTypes: SUPPORTED_QUESTION_TYPES,
  //   eventDetails: {
  //     id: eventId,
  //     hasEvent: !!event,
  //     rawEventType: event?.question_type
  //   },
  //   timestamp: new Date().toISOString()
  // });

  // // Log question type descriptions
  // console.log('Question Type Descriptions:', {
  //   default: 'Free-form text responses requiring sentiment and theme analysis',
  //   binary: 'Yes/No questions with distribution analysis',
  //   multiple: 'Single selection from multiple options with distribution analysis',
  //   multiple_rating: 'Multiple rating scales with distribution analysis',
  //   scale: 'Numeric scale responses with distribution analysis',
  //   scale_5: '5-point scale with distribution analysis',
  //   scale_10: '10-point scale with distribution analysis',
  //   stack_rank: 'Priority ordering with distribution analysis',
  //   timestamp: new Date().toISOString()
  // });

  const classes = useStyles();

  const handleError = useCallback((error) => {
    console.error('AIInsightsFeaturebasedV1 Error:', {
      error,
      questionType: mappedQuestionType,
      eventId,
      timestamp: new Date().toISOString()
    });
    setError(error);
    setIsLoading(false);
  }, [mappedQuestionType, eventId]);

  const handleLoadingComplete = useCallback(() => {
    // console.log('AIInsightsFeaturebasedV1 Loading Complete:', {
    //   questionType: mappedQuestionType,
    //   eventId,
    //   timestamp: new Date().toISOString()
    // });
    setIsLoading(false);
  }, [mappedQuestionType, eventId]);

  // Validate question type
  if (!mappedQuestionType || !SUPPORTED_QUESTION_TYPES.includes(mappedQuestionType)) {
    console.warn('Invalid Question Type Detected:', {
      receivedType: currentQuestionType,
      mappedType: mappedQuestionType,
      supportedTypes: SUPPORTED_QUESTION_TYPES,
      eventId,
      timestamp: new Date().toISOString()
    });
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">Invalid Question Type</Typography>
        <Typography variant="body2">
          Unable to generate AI insights: Question type '{currentQuestionType}' is not supported.
          Supported types: {SUPPORTED_QUESTION_TYPES.join(', ')}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6">Error Loading AI Insights</Typography>
        <Typography variant="body2">{error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box p={2}>
      {/* Header 
      
      <Box mb={3}>
        <Typography variant="h5" gutterBottom>
          AI-Enhanced Insights
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Advanced analysis powered by AI to help you understand your session data better.
        </Typography>
      </Box>
      */}

      {/* Main Content */}
      <AIInsightsPanelV2
        eventId={eventId}
        questionType={mappedQuestionType}
        data={event}
        stats={stats} // Pass the stats from Results tab
        onError={handleError}
        onLoadingComplete={handleLoadingComplete}
      />
    </Box>
  );
};

AIInsightsFeaturebasedV1.propTypes = {
  eventId: PropTypes.string.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    question_type: PropTypes.oneOf(SUPPORTED_QUESTION_TYPES)
  }),
  stats: PropTypes.object // Add stats to PropTypes
};

AIInsightsFeaturebasedV1.defaultProps = {
  event: null
};

export default AIInsightsFeaturebasedV1;
